// NotesApp source file : data.service.ts
// 🔧 COMPATIBLE DROP-IN FIXED VERSION

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  defer,
  firstValueFrom,
  of,
  from,
  throwError,
  Subject,
} from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isPendingLocalDeletion } from '../_util/trash.util';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

import { DataRemoteService } from './data-remote.service';
import { DataOfflineService } from './data-offline.service';
import { EnvironmentService } from './sync/environment.service';
import { DialogType, GlobalDialogService } from '../_service/global-dialog.service';
import { LogService } from '../_service/log.service';
import { SnackbarService } from './snackbar.service';
import Delta from 'quill-delta';
import { appendAiIndexActivity } from '../_util/ai-index-activity.util';
import { TAG_SCOPE, TagScope, filterCurrentTags } from '../_util/tag-scope.util';
import { NOTE_STATUSES } from '../_util/note-status.util';
import type { InlineTask } from '../_util/inline-task.util';
//import { SyncCoordinatorService } from './sync/sync-coordinator.service';


/* ============================================================
   PUBLIC INTERFACES — MUST REMAIN EXPORTED
   ============================================================ */

export interface AiSummary {
  summary_status: 'pending' | 'stale' | 'current' | 'failed' | 'disabled' | string;
  summary_stale?: boolean;
  summary_requested_at?: string;
  summary_generated_at?: string;
  summary_content_hash?: string;
  summary_note_revision?: string;
  summary_model?: string;
  summary_error?: string;
  summary_token_input?: number;
  summary_token_output?: number;
  summary_one_sentence?: string;
  summary_paragraph?: string;
  summary_bullets?: string[];
  summary_keywords?: string[];
  summary_for_embedding?: string;
}

export interface AiSummaryRegenerateResult {
  note_id: string;
  queued: boolean;
  source_hash?: string;
}

export interface AiSummaryStatusResult {
  note_id: string;
  summary_status: 'none' | 'pending' | 'stale' | 'current' | 'failed' | string;
  summary_generated_at: string | null;
  summary_model: string | null;
  summary_error: string | null;
  summary?: {
    one_sentence: string;
    paragraph: string;
    bullets: string[];
    keywords: string[];
  };
}

export interface Note {
  _id: string;
  _rev?: string;
  type: string;
  templateName?: string;
  NoteType: string;
  Title: string;
  Project: number;
  ProjectName: string;
  Status: string; // Draft | Active | Action Required | Inactive | Obsolete; legacy values tolerated (see _util/note-status.util.ts)
  /** Needs attention; missing means false. */
  actionRequired?: boolean;
  /** Low | Normal | High | Critical; missing means Normal. */
  priority?: 'Low' | 'Normal' | 'High' | 'Critical';
  dueDate?: string | null;
  completedOn?: string | null;
  /** Inline tasks (tasks/frontend-inline-tasks-phase-1-requirements.md), derived on save from the
   * task blocks in DescriptionHtml — the HTML is what syncs; missing means no tasks (see noteTasks()).
   * Not sent to the server until it accepts the field (see NOTE_WRITABLE_FIELDS). */
  tasks?: InlineTask[];
  ReferenceDate?: string;
  StartDate?: string;
  EndDate?: string;
  Confidential?: boolean;
  DescriptionDelta: Delta;
  CommentsDelta: Delta;
  DescriptionHtml: string;
  CommentsHtml: string;
  Keywords: string;
  AiKeywords: string;
  created_on: string;
  last_modified: string;
  Size?: number;
  Tags?: Array<string | Tag>;
  Attachments?: string[];
  RelatedNotes?: Array<string | RelatedNoteEntry>; // legacy bare-id entries are tolerated on read
  spaceId?: string; // which Space this note lives in; omitted means the user's own PRIVATE Space
  ai_summary?: AiSummary | null;
  remote_rev?: string;
  syncStatus?: string;
  syncTime?: string;
  /** Trash (tasks/delete_trash_client.md) — set by the server only; absent on older/local notes,
   * which means "not deleted". Never sent on writes (see DataRemoteService.sanitizeNoteForRemote). */
  deleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
  /** Local-only: a new note autosaved while its Space can still change — SyncCoordinator won't push
   * it until the first explicit save clears this. Never sent (not in NOTE_WRITABLE_FIELDS). */
  localDraft?: boolean;
  /** Local-only: the pending push is a tag-only change, so it keeps the note's Modified date
   * (see DataService.saveNoteTags). Cleared by any other save and once synced. Never sent. */
  keepLastModified?: boolean;
}

/** The subset of a note's editable fields captured in an opening snapshot —
 * mirrors exactly what applyChangesToNoteFromForm() reads/writes, so a
 * snapshot's content is always structurally comparable to (and restorable
 * into) a live note. Deliberately excludes Attachments (binaries aren't
 * duplicated) and identity/sync fields (_id, _rev, syncStatus, etc). */
export interface NoteEditSessionSnapshotContent {
  Title: string;
  DescriptionHtml: string;
  DescriptionDelta: Delta;
  CommentsHtml: string;
  CommentsDelta: Delta;
  Keywords: string;
  AiKeywords: string;
  Status: string;
  NoteType: string;
  Confidential?: boolean;
  ReferenceDate?: string;
  StartDate?: string;
  EndDate?: string;
  actionRequired?: boolean;
  priority?: 'Low' | 'Normal' | 'High' | 'Critical';
  dueDate?: string | null;
  completedOn?: string | null;
  Tags: Array<string | Tag>;
  RelatedNotes: Array<string | RelatedNoteEntry>;
  spaceId?: string;
}

/** Autosave Stage 2 — an IndexedDB-only "opening snapshot" of a note as it
 * stood when an edit session began, used to power "Discard changes since
 * opening…". Never synchronized to the backend. Keyed by (noteId, sessionId)
 * so multiple tabs/sessions editing the same note over time don't collide. */
export interface NoteEditSessionSnapshot {
  noteId: string;
  sessionId: string;
  openedAt: string;
  lastActiveAt: string;
  openingRev?: string;
  instanceId: string;
  status: 'active' | 'closed';
  lockRef?: string;
  content: NoteEditSessionSnapshotContent;
}

export interface SimilarNoteResult {
  _id?: string;
  id?: string;
  title?: string;
  Status?: string;
  updated_at?: string;
  modified_at?: string;
  modifiedDate?: string;
  last_modified?: string;
  final_score?: number;
  semantic_score?: number;
  recency_boost?: number;
  status_boost?: number;
  [key: string]: unknown;
}

export interface SimilarNotesResponse {
  results?: SimilarNoteResult[];
  [key: string]: unknown;
}

/** An entry in a note's RelatedNotes list: the linked note's id, plus its
 * explicit display order relative to the other related notes. A null
 * sortOrder sorts last — see related-notes.util.ts. */
export interface RelatedNoteEntry {
  /** ID of the referenced note (e.g. "note:abc123"). */
  id: string;
  /** Explicit display position; null sorts last. Always explicit after a save. */
  sortOrder: number | null;
}

export interface SuggestedTagResult {
  _id: string;
  name: string;
  Description?: string;
}

export interface SuggestTagsResponse {
  suggested_tags: SuggestedTagResult[];
  count: number;
}

export interface NoteType {
  ID: number;
  Name: string;
}

export interface Project {
  ID: number;
  Name: string;
}

export interface Status {
  Status: string;
}

export interface Tag {
  _id: string;
  _rev?: string;
  type: 'tag';
  name: string;
  description?: string;
  tagType: string;
  slug: string;
  color?: string;
  icon?: string;
  parentTagId: string | null;
  sortOrder?: number;
  isActive: boolean;
  /** PRIVATE or SPACE (see _util/tag-scope.util.ts). A tag without a valid scope is obsolete and dropped. */
  scope?: TagScope;
  /** The Space a SPACE tag belongs to. Private ownership is server-derived — no userId here. */
  spaceId?: string | null;
}

export interface TagMutationPayload {
  name: string;
  description?: string;
  tagType: string;
  slug: string;
  color?: string;
  icon?: string;
  parentTagId?: string | null;
  sortOrder?: number;
  isActive: boolean;
  /** Creates/updates a SPACE tag in this Space; omit for a PRIVATE tag. The server decides the
   * scope from this alone — scope/ownership are never sent. */
  spaceId?: string;
}

export interface BulkStatusResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

/** tasks/space-move-client.md — POST /api/notes/bulk/move result (HTTP 200 with status "success" or
 * "partial"; the whole request is rejected up front, with nothing moved, on validation errors). */
export interface BulkMoveResult {
  destinationSpaceId: string;
  requested: number;
  moved: number;
  movedNoteIds: string[];
  errors: Array<{ note_id: string; error: string }>;
  /** Notes left in BOTH Spaces by a failed move whose rollback also failed (server-logged). */
  duplicated: string[];
}

export interface BulkDeleteResult {
  updatedCount?: number;
  deletedCount?: number;
  deleted?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkTagAddResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkTagRemoveResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface ApiResponse<T> {
  /** 'partial': a bulk request where some items failed (see e.g. TrashBulkResult.errors). */
  status: 'success' | 'error' | 'warning' | 'partial';
  data?: T;
  error?: { code: number; message: string };
  warnings?: string[];
}

export interface BulkImportResult {
  index: number;
  status: 'success' | 'error';
  data?: Note;
  error?: { code: number; message: string; details?: string[] };
  warnings?: string[];
}

export interface BulkImportSummary {
  total: number;
  imported: number;
  warned: number;
  failed: number;
}

export interface BulkImportResponse {
  status: 'success' | 'partial' | 'error';
  summary: BulkImportSummary;
  results: BulkImportResult[];
}

export interface BulkStatusResult {
  updatedCount?: number;
  message?: string;
}

export interface BulkDeleteResult {
  updatedCount?: number;
  updated?: number;        // ✅ add this so union access works
  deletedCount?: number;
  deleted?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

/** One entry of GET /api/trash — summary fields only (no content). */
export interface TrashItem {
  _id: string;
  Title?: string;
  NoteType?: string;
  Status?: string;
  spaceId?: string | null;
  deleted?: boolean;
  /** Server time of the move to Trash — authoritative for the retention display. */
  deletedAt?: string | null;
  deletedBy?: string | null;
  Size?: number;
}

/** Result of the Trash bulk endpoints (restore / permanent delete / Empty Trash). */
export interface TrashBulkResult {
  requested: number;
  restored?: number;
  restoredNoteIds?: string[];
  deleted?: number;
  deletedNoteIds?: string[];
  errors?: Array<{ note_id?: string; error?: string }>;
}

export interface DbInfo {
  db_name: string;
  total: number;
  active: number;
  deleted_status: number;
  tombstones: number;
  update_seq: string | number;
  generated_at: string;
  status_field_present?: boolean; // ✅ add this (your offline service writes it)
}


/* ============================================================
   DATA SERVICE
   ============================================================ */

@Injectable({ providedIn: 'root' })
export class DataService {
  private readonly baseUrl = environment.apiBaseUrl || '/api';
  public isOnline = navigator.onLine;

  /** Canonical in-memory state */
  public allNotes: Note[] = [];
  public allTags: Tag[] = [];
  public allTemplates: Note[] = [];

  private dataLoadedSubject = new BehaviorSubject<boolean>(false);
  public dataLoaded$ = this.dataLoadedSubject.asObservable();
  private dataReloadedSubject = new BehaviorSubject<Date | null>(null);
  public dataReloaded$ = this.dataReloadedSubject.asObservable();
  private templatesLoadedSubject = new BehaviorSubject<boolean>(false);
  public templatesLoaded$ = this.templatesLoadedSubject.asObservable();

  private isSyncing = false;

  private upsertNoteInMemory(note: Note): void {
    if (!note || !note._id) {
      return;
    }

    const isTemplate = note.type === 'template' || String(note._id).startsWith('template:');
    if (isTemplate) {
      const templateIndex = this.allTemplates.findIndex((item) => item._id === note._id);
      if (templateIndex >= 0) {
        this.allTemplates[templateIndex] = { ...this.allTemplates[templateIndex], ...note };
      } else {
        this.allTemplates = [...this.allTemplates, note];
      }
      return;
    }

    const noteIndex = this.allNotes.findIndex((item) => item._id === note._id);
    if (noteIndex >= 0) {
      this.allNotes[noteIndex] = { ...this.allNotes[noteIndex], ...note };
    } else {
      this.allNotes = [...this.allNotes, note];
    }
  }

  private emitLocalDataChanged(): void {
    this.dataLoadedSubject.next(true);
    this.dataReloadedSubject.next(new Date());
  }

  private triggerNoteIndexRefresh(note: Note): void {
    if (!this.isOnline || !note?._id || note.type !== 'note') {
      return;
    }

    const noteId = encodeURIComponent(note._id);
    const reindexUrl = `${this.baseUrl}/ai/reindex-note/${noteId}`;
    const legacyReindexUrl = `${this.baseUrl}/rag/reindex/${noteId}`;

    this.http
      .post<ApiResponse<{ message?: string }>>(reindexUrl, {})
      .pipe(
        catchError((err) => {
          const status = Number((err as any)?.status ?? 0);
          // Backward compatibility: if the new endpoint is unavailable, try the legacy endpoint.
          if (status === 404 || status === 405) {
            return this.http.post<ApiResponse<{ message?: string }>>(legacyReindexUrl, null);
          }
          return throwError(() => err);
        }),
      )
      .subscribe({
      next: (res) => {
        appendAiIndexActivity({
          kind: 'note_reindex',
          timestamp: new Date().toISOString(),
          status: 'success',
          source: 'note_save',
          noteId: note._id,
          noteTitle: note.Title,
          details: String(res?.data?.message ?? 'Note index refresh requested.').trim(),
        });
      },
      error: (err) => {
        const errorMessage =
          String(err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Note index refresh failed.')
            .trim();
        appendAiIndexActivity({
          kind: 'note_reindex',
          timestamp: new Date().toISOString(),
          status: 'failed',
          source: 'note_save',
          noteId: note._id,
          noteTitle: note.Title,
          error: errorMessage,
        });
      },
    });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private dataRemoteService: DataRemoteService,
    private dataOfflineService: DataOfflineService,
    private environmentService: EnvironmentService,
    private gDS: GlobalDialogService,
    private logs: LogService,
    private snackbarService: SnackbarService,
    //private readonly syncCoordinator: SyncCoordinatorService,
  ) {
    this.environmentService.online$.subscribe((online) => {
      this.isOnline = online;
      this.onOnlineStatusChange(online);
    });

    // 🔥 FIX: baseline completion reloads IndexedDB into memory
    this.dataOfflineService.onBaselineCompleted().subscribe(async () => {
      this.logs.append('synch', '[DataService] baseline completed → reload');
      await this.reloadFromIndexedDB();
    });

    this.dataOfflineService.onIncrementalChangesApplied().subscribe(async () => {
      this.logs.append('synch', '[DataService] incremental changes applied → reload');
      await this.reloadFromIndexedDB();
    });
  }


  async initialize(): Promise<void> {
    await this.reloadFromIndexedDB();
    // Load templates at startup for instant access
    this.loadTemplatesIntoCache();
  }


    // ✅ Single public sync entry point
//   syncAllChanges(
//     reason: 'manual' | 'online' | 'debug' = 'manual',
//   ): Promise<boolean> {
//     return this.syncCoordinator.forceSync(reason);
//   }

// src/app/_service/data.service.ts

onOnlineStatusChange(isOnline: boolean): void {
  if (!isOnline) {
    console.log('[DataService] App is offline');
    return;
  }

  console.log('[DataService] App is back online → requesting sync');
  //void this.syncAllChanges('online');
}

fetchRemoteDbInfoAndLogActive(): void {
  this.dataRemoteService.fetchDbInfoAndLogActive();
}

fetchOfflineDbInfoAndLogActive(): void {
  this.dataOfflineService.fetchDbInfoAndLogActive();
}

async clearOfflineStorage(): Promise<void> {
  await this.dataOfflineService.clearOfflineAndLocal();
  this.allNotes = [];
  this.dataLoadedSubject.next(false);
  this.logs.append('synch', '[DataService] Offline storage cleared');
}


  /* ============================================================
     🔥 FIXED: INITIALIZE (startup.service depends on this)
     ============================================================ */

  async refreshLocalData(): Promise<void> {
    await this.reloadFromIndexedDB();
  }

  /* ============================================================
     🔥 FIXED: reloadFromIndexedDB
     ============================================================ */

  private async reloadFromIndexedDB(): Promise<void> {
    //this.logs.append('synch', '[DataService] reloadFromIndexedDB()');

    const notes = await this.dataOfflineService.getAllNotes();

    // Notes deleted on this device but not yet synced are already "in Trash" for the user.
    this.allNotes = notes.filter((n) => n.type === 'note' && !isPendingLocalDeletion(n));

    // 🔥 Per-note logging (your explicit requirement)
    // for (const n of this.allNotes) {
    //   this.logs.append(
    //     'synch',
    //     `[LOAD] ${n._id} — ${n.Title || '(no title)'}`
    //   );
    // }

    // this.logs.append(
    //   'synch',
    //   `[DataService] Loaded ${this.allNotes.length} notes`,
    // );

    this.dataLoadedSubject.next(true);
    this.dataReloadedSubject.next(new Date());
  }

  /* ============================================================
     READ OPERATIONS (unchanged semantics)
     ============================================================ */

  getAllNotes(): Observable<ApiResponse<Note[]>> {
    return defer(() => from(this.dataOfflineService.getAllNotes())).pipe(
      map((notes) => ({
        status: 'success' as const,
        // Pending local deletions (moved to Trash, not yet synced) never show in normal views.
        data: notes.filter((n) => n.type === 'note' && !isPendingLocalDeletion(n)),
      })),
      catchError((err) =>
        of({
          status: 'error' as const,
          error: { code: 0, message: String(err) },
        }),
      ),
    );
  }

  getAllTags(): Observable<ApiResponse<Tag[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/tags`).pipe(
      map((resp) => {
        // Only current (PRIVATE / SPACE) tags; an unscoped tag is obsolete pre-migration data.
        const tags = filterCurrentTags(Array.isArray(resp.data) ? resp.data.map((tag) => this.normalizeTagFromApi(tag)) : []);
        this.allTags = tags;
        // Offline-first: cache the canonical tag list (with real names) locally so it
        // remains fully available - not just tag ids - when the server is unreachable.
        void this.dataOfflineService.syncTagsFromServer(tags).catch((cacheErr) =>
          console.warn('[DataService] Failed to cache tags to IndexedDB:', cacheErr),
        );
        return {
          ...resp,
          data: tags,
        } as ApiResponse<Tag[]>;
      }),
      catchError((err: any) =>
        // Offline-first fallback: while offline (or on any request failure), use the
        // locally cached tag list (real names/colors, synced the last time the server
        // was reachable) rather than losing the tag names entirely.
        from(this.dataOfflineService.getAllTagsLocal()).pipe(
          map((cached) => {
            const cachedTags = filterCurrentTags(cached);
            if (cachedTags.length > 0) {
              console.warn('[DataService] Tag request failed; using locally cached tags instead:', err);
              this.allTags = cachedTags;
              return { status: 'success', data: cachedTags } as ApiResponse<Tag[]>;
            }
            return {
              status: 'error',
              error: {
                code: err?.status ?? 0,
                message: err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to load tags.',
              },
            } as ApiResponse<Tag[]>;
          }),
        ),
      ),
    );
  }

  createTag(payload: TagMutationPayload): Observable<ApiResponse<Tag>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/tags`, this.serializeTagMutationPayload(payload)).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag && !payload.spaceId) {
          this.upsertTagCache(tag);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: this.extractTagApiErrorMessage(err, 'Failed to create tag.'),
          },
        }),
      ),
    );
  }

  updateTag(tagId: string, payload: TagMutationPayload): Observable<ApiResponse<Tag>> {
    return this.http.put<ApiResponse<any>>(
      `${this.baseUrl}/tags/${encodeURIComponent(tagId)}`,
      this.serializeTagMutationPayload(payload),
    ).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag && !payload.spaceId) {
          this.upsertTagCache(tag);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: this.extractTagApiErrorMessage(err, 'Failed to update tag.'),
          },
        }),
      ),
    );
  }

  /** `spaceId` for a SPACE tag; omit for a PRIVATE one (the server routes by it). */
  deleteTag(tagId: string, spaceId?: string): Observable<ApiResponse<Tag>> {
    const query = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/tags/${encodeURIComponent(tagId)}${query}`).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag) {
          this.upsertTagCache(tag);
        } else {
          this.allTags = this.allTags.filter((existingTag) => existingTag._id !== tagId);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to delete tag.',
          },
        }),
      ),
    );
  }

  getTagInfo(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/tags/info`).pipe(
      catchError((err: any) =>
        of<ApiResponse<any>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to load tag info.',
          },
        }),
      ),
    );
  }

  getNote(id: string): Observable<ApiResponse<Note | undefined>> {
    return defer(() => from(this.dataOfflineService.getNote(id))).pipe(
      map((note) =>
        note && !isPendingLocalDeletion(note) // a note moved to Trash on this device is gone
          ? { status: 'success' as const, data: note }
          : {
              status: 'error' as const,
              error: { code: 404, message: 'Note not found' },
            },
      ),
    );
  }

  /* ============================================================
     STATIC LOOKUPS (used all over UI)
     ============================================================ */

  getAllProjects(): Project[] {
    return [
      { ID: 1, Name: 'Art / Photography' },
      { ID: 2, Name: 'At Home' },
      { ID: 3, Name: 'Career' },
      { ID: 4, Name: 'Work' },
    ];
  }

  /** The selectable note statuses (current lifecycle only — legacy values are never offered). */
  getAllStatuses(): string[] {
    return [...NOTE_STATUSES];
  }

  getAllNoteTypes(): string[] {
    return ['To Do', 'Event', 'Journal', 'Knowledgebase'];
  }

  /* ============================================================
     STUBS / LEGACY — preserved for compatibility
     ============================================================ */

//   async syncAllChanges(): Promise<void> {
//     this.logs.append('synch', 'syncAllChanges() called (no-op)');
//   }

  createNote() {
    return this.dataOfflineService.createNote();
  }

  createNoteFromTemplate(t: Note) {
    return this.dataOfflineService.createNoteFromTemplate(t);
  }

  createNoteCopy(note: Note) {
    return this.dataOfflineService.createNoteCopy(note);
  }

  createTemplateFromNote(n: Note, name: string) {
    return this.dataOfflineService.createTemplateFromNote(n, name);
  }




  
  updateNote(n: Note): Observable<ApiResponse<Note>> {
    this.logs.append('synch', `[SAVE] start ${n._id} (has_rev=${!!n._rev})`);

    return defer(() => from(this.dataOfflineService.saveNote(n, false))).pipe(
      map(() => {
        this.logs.append('synch', `[SAVE] local IndexedDB write complete ${n._id}`);
        this.upsertNoteInMemory(n);
        this.emitLocalDataChanged();
        this.logs.append('synch', `[SAVE] local UI refresh emitted ${n._id}`);

        if (!this.isOnline) {
          this.logs.append('synch', `[SAVE] remote skipped (offline) ${n._id}`);
          return {
            status: 'warning' as const,
            data: n,
            error: { code: 0, message: 'Saved locally. Offline; will sync later.' },
          };
        }

        // The write is already durable in IndexedDB at this point — don't block
        // the caller (e.g. the Save button) on the remote round-trip. Push it in
        // the background; if it fails, the note stays dirty and SyncCoordinator's
        // own push cycle (with its per-note retry backoff) will pick it up.
        this.pushNoteInBackground(n);
        return { status: 'success' as const, data: n };
      }),
      catchError((err: any) => {
        this.logs.append('synch', `[SAVE] local write failed ${n._id}: ${err?.message ?? String(err)}`);
        return of<ApiResponse<Note>>({
          status: 'error',
          error: { code: 0, message: err?.message ?? String(err) },
        });
      }),
    );
  }

  /**
   * Lightweight local-only save used by autosave. Writes to IndexedDB and
   * marks the note dirty for the existing background sync cycle, but does
   * NOT push to the remote immediately (unlike updateNote()) — autosave
   * fires every few seconds and must never require server availability or
   * trigger a remote round-trip (and any backend AI/indexing work) more
   * often than the existing SyncCoordinator push cycle already does.
   */
  async autosaveNote(n: Note): Promise<void> {
    await this.dataOfflineService.saveNote(n, false);
    this.upsertNoteInMemory(n);
    this.emitLocalDataChanged();
  }

  /**
   * Saves a change to a note's Space tags (Note.Tags) the way autosaveNote() does — IndexedDB first,
   * pushed by the normal sync cycle, works offline — but without changing the note's Modified date,
   * neither locally nor when it is pushed: adding or removing a tag is not an edit of the note.
   */
  async saveNoteTags(n: Note): Promise<void> {
    await this.dataOfflineService.saveNote(n, false, { keepLastModified: true });
    this.upsertNoteInMemory(n);
    this.emitLocalDataChanged();
  }

  /** Autosave Stage 2 — opening-snapshot CRUD. IndexedDB-only, never synced. */
  saveEditSessionSnapshot(snapshot: NoteEditSessionSnapshot): Promise<void> {
    return this.dataOfflineService.saveEditSessionSnapshot(snapshot);
  }

  getEditSessionSnapshot(noteId: string, sessionId: string): Promise<NoteEditSessionSnapshot | undefined> {
    return this.dataOfflineService.getEditSessionSnapshot(noteId, sessionId);
  }

  deleteEditSessionSnapshot(noteId: string, sessionId: string): Promise<void> {
    return this.dataOfflineService.deleteEditSessionSnapshot(noteId, sessionId);
  }

  getAllEditSessionSnapshots(): Promise<NoteEditSessionSnapshot[]> {
    return this.dataOfflineService.getAllEditSessionSnapshots();
  }

  private pushNoteInBackground(n: Note): void {
    const shouldCreate = !n._rev;

    const remoteCall$: Observable<Note> = shouldCreate
      ? (() => {
          this.logs.append('synch', `[SAVE] remote create attempt ${n._id}`);
          return this.dataRemoteService.importNoteFromOffline(n).pipe(
            concatMap(({ note: created, warnings }) => {
              if (warnings.length) {
                this.logs.append('synch', `[SAVE] ${n._id} warnings: ${warnings.join(' ')}`);
              }
              // Backend response is authoritative for persisted fields (for example Size).
              const createdNote: Note = { ...n, ...(created ?? {}) };
              return from(this.dataOfflineService.saveNote(createdNote, true)).pipe(map(() => createdNote));
            }),
          );
        })()
      : (() => {
          this.logs.append('synch', `[SAVE] remote update attempt ${n._id} rev=${n._rev ?? 'n/a'}`);
          return from(this.dataRemoteService.updateNote(n));
        })();

    remoteCall$.subscribe({
      next: (updated) => {
        this.logs.append('synch', `[SAVE] remote push success ${n._id} rev=${updated._rev ?? 'n/a'}`);
        this.upsertNoteInMemory(updated);
        this.emitLocalDataChanged();
        this.triggerNoteIndexRefresh(updated);
        this.logs.append('synch', `[SAVE] local UI refresh emitted ${n._id}`);
      },
      error: (err: any) => {
        // Try to extract backend error details for the log; the note remains
        // dirty in IndexedDB so SyncCoordinator will retry it automatically.
        let message = err?.message ?? 'Remote push failed.';
        if (err?.error) {
          if (typeof err.error === 'object' && err.error.message) {
            message = err.error.message;
          } else if (typeof err.error === 'string') {
            message = err.error;
          }
        }
        this.logs.append('synch', `[SAVE] remote push failed ${n._id}: ${message}`);
        this.snackbarService.showErrorMessage(
          `"${n.Title || 'Note'}" was saved locally but could not sync to the server yet. It will retry automatically.`,
        );
      },
    });
  }

/**
 * Delete = move to Trash (tasks/delete_trash_client.md), offline-first:
 *  1. the local copy becomes a deletion tombstone (syncStatus 'deleted') and disappears from every
 *     normal view immediately (see isPendingLocalDeletion());
 *  2. online, the existing DELETE /notes/<id> is sent right away — the server now soft-deletes
 *     (Trash); once it confirms, the local copy and its cached attachments are removed;
 *  3. offline, or on a transient failure, the tombstone stays and SyncCoordinatorService pushes it
 *     later, so the delete is never lost and a stale copy can't come back.
 * A note that never reached the server (new / local draft) has nothing to trash: it is just removed.
 * If the server refuses (e.g. 403, no delete rights in the Space) the local note is put back.
 */
deleteNote(note: Note): Observable<ApiResponse<Note>> {
  return defer(() => from(this.moveNoteToTrash(note)));
}

private async moveNoteToTrash(note: Note): Promise<ApiResponse<Note>> {
  const id = note?._id;
  if (!id) return { status: 'error', error: { code: 400, message: 'No note to delete.' } };

  // Never on the server: nothing to trash, just drop the local copy.
  if (note.syncStatus === 'new' || note.localDraft) {
    try {
      await this.dataOfflineService.deleteNote(id);
      await this.dataOfflineService.deleteAttachmentsByNote(id);
    } catch (err: any) {
      return { status: 'error', error: { code: 0, message: `Failed to delete locally: ${err?.message ?? String(err)}` } };
    }
    this.removeNotesFromMemory([id]);
    this.emitLocalDataChanged();
    return { status: 'success', data: note };
  }

  let previous: Note;
  try {
    previous = await this.dataOfflineService.markNoteAsDeleted(id);
  } catch (err: any) {
    return { status: 'error', error: { code: 0, message: `Failed to delete locally: ${err?.message ?? String(err)}` } };
  }
  this.removeNotesFromMemory([id]);
  this.emitLocalDataChanged();
  this.logs.append('synch', `[TRASH] local tombstone ${id}`);

  if (!this.isOnline) {
    return {
      status: 'warning',
      data: note,
      error: { code: 0, message: 'Moved to Trash on this device. It will sync when you are back online.' },
    };
  }

  try {
    await firstValueFrom(this.dataRemoteService.deleteNote(id, note._rev || '', note.spaceId));
  } catch (err: any) {
    const status = Number(err?.status ?? 0);
    if (status === 404) {
      // Already gone from the server's normal notes (trashed or purged elsewhere): same end state.
      await this.purgeLocalNotes([id]);
      return { status: 'success', data: note };
    }
    if (status === 401 || status === 403 || status === 400) {
      // The server refused this delete — put the note back rather than retry forever.
      await this.dataOfflineService.restoreLocalNote(previous);
      this.upsertNoteInMemory(previous);
      this.emitLocalDataChanged();
      return { status: 'error', error: { code: status, message: err?.message || 'The note could not be deleted.' } };
    }
    // Network/server trouble: keep the tombstone, the sync push retries it.
    this.logs.append('synch', `[TRASH] remote delete deferred ${id}: ${err?.message ?? err}`);
    return {
      status: 'warning',
      data: note,
      error: { code: status, message: 'Moved to Trash on this device. The server will be updated on the next sync.' },
    };
  }

  await this.purgeLocalNotes([id]);
  this.logs.append('synch', `[TRASH] moved to Trash on server ${id}`);
  return { status: 'success', data: note };
}

/** Removes notes (and their cached attachments) from IndexedDB and memory — after the server
 * confirmed a move to Trash, or a permanent delete. */
private async purgeLocalNotes(noteIds: string[]): Promise<void> {
  for (const id of noteIds) {
    try {
      await this.dataOfflineService.deleteNote(id);
      await this.dataOfflineService.deleteAttachmentsByNote(id);
    } catch (err) {
      console.warn('[DataService] could not purge local copy of', id, err);
    }
  }
  this.removeNotesFromMemory(noteIds);
}

private removeNotesFromMemory(noteIds: string[]): void {
  const ids = new Set(noteIds);
  this.allNotes = this.allNotes.filter((n) => !ids.has(n._id));
  this.allTemplates = this.allTemplates.filter((n) => !ids.has(n._id));
}

/** Moves several notes to Trash, each exactly like deleteNote() (offline-first, one tombstone each). */
async moveNotesToTrash(noteIds: string[]): Promise<{ moved: string[]; queued: string[]; failed: Array<{ id: string; message: string }> }> {
  const moved: string[] = [];
  const queued: string[] = [];
  const failed: Array<{ id: string; message: string }> = [];
  for (const id of noteIds) {
    const note = (await this.dataOfflineService.getNote(id)) ?? this.allNotes.find((n) => n._id === id);
    if (!note) {
      failed.push({ id, message: 'Note not found on this device.' });
      continue;
    }
    const result = await this.moveNoteToTrash(note);
    if (result.status === 'success') moved.push(id);
    else if (result.status === 'warning') queued.push(id);
    else failed.push({ id, message: result.error?.message ?? 'Delete failed.' });
  }
  return { moved, queued, failed };
}

// ---- Trash page operations (online only: Trash lives on the server) ----

getTrash(): Observable<ApiResponse<TrashItem[]>> {
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Trash is only available while online.' } });
  }
  return this.dataRemoteService.getTrash();
}

/** Restores one note. It reappears on this device through the next sync pull (its normal path). */
restoreFromTrash(item: Pick<TrashItem, '_id' | 'spaceId'>): Observable<ApiResponse<{ _id: string }>> {
  return this.dataRemoteService.restoreNote(item._id, item.spaceId);
}

restoreManyFromTrash(noteIds: string[]): Observable<ApiResponse<TrashBulkResult>> {
  return this.dataRemoteService.restoreNotes(noteIds);
}

/** Permanently deletes one trashed note, then drops any stale local copy of it. */
permanentlyDeleteFromTrash(item: Pick<TrashItem, '_id' | 'spaceId'>): Observable<ApiResponse<{ _id: string }>> {
  return this.dataRemoteService.permanentlyDeleteNote(item._id, item.spaceId).pipe(
    concatMap((resp) => (resp.status === 'success' ? from(this.purgeLocalNotes([item._id])).pipe(map(() => resp)) : of(resp))),
  );
}

permanentlyDeleteManyFromTrash(noteIds: string[]): Observable<ApiResponse<TrashBulkResult>> {
  return this.dataRemoteService.permanentlyDeleteNotes(noteIds).pipe(
    concatMap((resp) => from(this.purgeLocalNotes(resp.data?.deletedNoteIds ?? [])).pipe(map(() => resp))),
  );
}

emptyTrash(): Observable<ApiResponse<TrashBulkResult>> {
  return this.dataRemoteService.emptyTrash().pipe(
    concatMap((resp) => from(this.purgeLocalNotes(resp.data?.deletedNoteIds ?? [])).pipe(map(() => resp))),
  );
}

bulkSetStatus(
  noteIds: string[],
  status: string,
  updateTimestamp: boolean,
): Observable<ApiResponse<BulkStatusResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!status) {
    return of({ status: 'error', error: { code: 400, message: 'No status provided.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk update requires online.' } });
  }

  return this.dataRemoteService.bulkSetStatus(noteIds, status, updateTimestamp).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkStatusResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk status update failed.' },
      }),
    ),
  );
}


deleteNotesOffline(noteIds: string[]): Promise<void> {
  return this.dataOfflineService.deleteNotes(noteIds);
}


/** Passthrough to DataRemoteService.importNotesBulk() — the caller (the
 * "Import JSON Note(s)..." dialog) is responsible for falling back to the
 * offline-only save path when this rejects (offline, timeout, or any other
 * network failure), so this deliberately throws rather than swallowing the
 * error into an ApiResponse-shaped result. */
importNotesBulk(notes: Note[]): Observable<BulkImportResponse> {
  if (!notes?.length) {
    return throwError(() => new Error('No notes to import.'));
  }
  if (!this.isOnline) {
    return throwError(() => new Error('Offline. Bulk import requires online.'));
  }

  return this.dataRemoteService.importNotesBulk(notes);
}


// ---------------------------------------------------------------------------
// Move between Spaces (tasks/space-move-client.md)
// ---------------------------------------------------------------------------

/** Why `note` can't be moved right now, or null if it can. The server moves ITS copy, so a note with
 * local changes it hasn't received yet (or one it has never received) must sync first. */
moveBlockedReason(note: Note | undefined): string | null {
  if (!this.isOnline) return 'Moving a note to another Space requires a connection.';
  if (!note?._id) return 'Note not found.';
  if (note.localDraft || note.syncStatus === 'new' || note.Status === 'Temp') return 'Save the note before moving it.';
  if (note.syncStatus && note.syncStatus !== 'synced') return 'This note has changes that haven’t synced yet — try again once it has synced.';
  return null;
}

/** Moves one note via POST /api/notes/<id>/move. On success the local copy is updated in place — new
 * Space, the server's new _rev, no Space tags (they don't transfer) — without a normal save, so
 * last_modified is untouched. On failure nothing local changes. */
moveNote(note: Note, destinationSpaceId: string): Observable<ApiResponse<Note>> {
  const blocked = this.moveBlockedReason(note);
  if (blocked) {
    return of({ status: 'error', error: { code: 0, message: blocked } });
  }
  return this.dataRemoteService.moveNote(note._id, destinationSpaceId).pipe(
    concatMap((resp) => {
      if (resp.status !== 'success') return of(resp);
      return from(this.applyMovedNoteLocally(note._id, destinationSpaceId, resp.data)).pipe(
        map((moved) => ({ status: 'success' as const, data: moved ?? note })),
      );
    }),
  );
}

/** Moves several notes in ONE request (POST /api/notes/bulk/move); they may come from different
 * Spaces. Only the ids the server reports as moved are updated locally. */
bulkMoveNotes(noteIds: string[], destinationSpaceId: string): Observable<ApiResponse<BulkMoveResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Moving notes to another Space requires a connection.' } });
  }
  return this.dataRemoteService.bulkMoveNotes(noteIds, destinationSpaceId).pipe(
    concatMap((resp) => {
      const movedIds = resp.data?.movedNoteIds ?? [];
      if (resp.status === 'error' || !movedIds.length) return of(resp);
      return from(Promise.all(movedIds.map((id) => this.applyMovedNoteLocally(id, destinationSpaceId)))).pipe(
        map(() => resp),
      );
    }),
  );
}

/** Reflects a completed server-side move in IndexedDB + memory. Deliberately NOT a normal save:
 * last_modified is kept exactly as it was and the note stays "synced" (no push is queued). The bulk
 * endpoint returns no new revisions, so there the next pull of the destination Space refreshes _rev. */
private async applyMovedNoteLocally(noteId: string, destinationSpaceId: string, serverDoc?: Partial<Note>): Promise<Note | undefined> {
  const local = await this.dataOfflineService.getNote(noteId);
  if (!local) return undefined;
  const moved: Note = {
    ...local,
    ...(serverDoc ?? {}),
    _id: local._id,
    spaceId: destinationSpaceId,
    Tags: [],
    last_modified: local.last_modified,
  };
  await this.dataOfflineService.saveNote(moved, true);
  this.upsertNoteInMemory(moved);
  this.emitLocalDataChanged();
  return moved;
}

/**
 * Bulk Delete = move every selected note to Trash, each through deleteNote()'s offline-first path
 * (the server has no bulk soft-delete endpoint; /notes/bulk/delete is not used). Works offline too:
 * the moves are queued as tombstones and pushed by the sync.
 */
bulkDelete(noteIds: string[]): Observable<ApiResponse<BulkDeleteResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  return defer(() => from(this.moveNotesToTrash(noteIds))).pipe(
    map(({ moved, queued, failed }): ApiResponse<BulkDeleteResult> => {
      const done = moved.length + queued.length;
      const message =
        `Moved ${done} note${done === 1 ? '' : 's'} to Trash` +
        (queued.length ? ` (${queued.length} will sync later)` : '') +
        (failed.length ? `; ${failed.length} could not be deleted` : '') +
        '.';
      if (done === 0) {
        return { status: 'error', error: { code: 0, message: failed[0]?.message ?? 'Bulk delete failed.' } };
      }
      return {
        status: failed.length ? 'partial' : 'success',
        data: { deleted: done, deletedCount: done, failedIds: failed.map((f) => f.id), message },
      };
    }),
    catchError((err: any) =>
      of<ApiResponse<BulkDeleteResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk delete failed.' },
      }),
    ),
  );
}

bulkAddTags(noteIds: string[], tagIds: string[]): Observable<ApiResponse<BulkTagAddResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!tagIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No tags selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk tag update requires online.' } });
  }

  return this.dataRemoteService.bulkAddTags(noteIds, tagIds).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkTagAddResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk add tags failed.' },
      }),
    ),
  );
}

bulkRemoveTags(
  noteIds: string[],
  tagIds: string[],
  removeAll: boolean,
): Observable<ApiResponse<BulkTagRemoveResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!removeAll && !tagIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No tags selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk tag update requires online.' } });
  }

  return this.dataRemoteService.bulkRemoveTags(noteIds, tagIds, removeAll).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkTagRemoveResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk remove tags failed.' },
      }),
    ),
  );
}


  searchNotes(q: string) {
    return from(this.dataOfflineService.searchNotes(q));
  }

  generateKeywords(payload: any) {
    return this.http.post<ApiResponse<any>>('/api/ai/generate-keywords', payload);
  }

  suggestTags(payload: { note_id: string }): Observable<ApiResponse<SuggestTagsResponse>> {
    return this.http.post<ApiResponse<SuggestTagsResponse>>('/api/ai/suggest-tags', payload);
  }

  regenerateAiSummary(noteId: string): Observable<ApiResponse<AiSummaryRegenerateResult>> {
    const encoded = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.post<ApiResponse<AiSummaryRegenerateResult>>(`/api/notes/${encoded}/summary/regenerate`, {}).pipe(
      catchError((err: any) =>
        of({
          status: 'error' as const,
          error: { code: err?.status ?? 0, message: err?.error?.message ?? err?.message ?? 'AI summary request failed.' },
        }),
      ),
    );
  }

  getAiSummaryStatus(noteId: string): Observable<ApiResponse<AiSummaryStatusResult>> {
    const encoded = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.get<ApiResponse<AiSummaryStatusResult>>(`/api/notes/${encoded}/summary/status`).pipe(
      catchError((err: any) =>
        of({
          status: 'error' as const,
          error: { code: err?.status ?? 0, message: err?.error?.message ?? err?.message ?? 'Failed to check AI summary status.' },
        }),
      ),
    );
  }

  findSimilarNotes(noteId: string): Observable<ApiResponse<SimilarNoteResult[]>> {
    const rawId = String(noteId ?? '').trim();
    const apiNoteId = rawId.startsWith('note:')
      ? rawId.slice('note:'.length)
      : rawId.startsWith('template:')
        ? rawId.slice('template:'.length)
        : rawId;
    const encoded = encodeURIComponent(apiNoteId);
    return this.http.get<ApiResponse<SimilarNoteResult[]>>(`${this.baseUrl}/notes/${encoded}/similar`);
  }

  putNoteToCouchDB(note: Note) {
    return this.dataRemoteService.putNoteToCouchDB(note);
  }

  cleanupCouchDBConflicts(id: string) {
    return this.dataRemoteService.cleanupCouchDBConflicts(id);
  }

  saveNoteOffline(note: Note, overwrite: boolean) {
    return this.dataOfflineService.saveNote(note, overwrite).then(() => {
      this.upsertNoteInMemory(note);
      this.emitLocalDataChanged();
    });
  }

  getTemplates() {
    return this.dataRemoteService.getTemplates();
  }

  /**
   * Get templates from cache (instant access)
   * Returns cached templates if available, otherwise empty array
   */
  getTemplatesFromCache(): Note[] {
    return this.allTemplates;
  }

  /**
   * Check if templates have been loaded
   */
  areTemplatesLoaded(): boolean {
    return this.templatesLoadedSubject.value;
  }

  /**
   * Load templates into cache for instant access
   * Called at startup and after template modifications
   *
   * Offline-first: template:* docs are kept current in IndexedDB by the same
   * sync pipeline that syncs notes, so seed the cache from there first (works
   * offline), then best-effort refresh from the server. A failed/offline
   * network refresh must never reduce the cache to an empty list.
   */
  private loadTemplatesIntoCache(): void {
    this.dataOfflineService
      .getAllNotes()
      .then((notes) => {
        const localTemplates = (notes ?? []).filter((n) => n.type === 'template' && !isPendingLocalDeletion(n));
        this.allTemplates = localTemplates;
        this.templatesLoadedSubject.next(true);
        console.log(`[DataService] Loaded ${localTemplates.length} template(s) from IndexedDB`);
      })
      .catch((err) => {
        console.error('[DataService] Failed to load templates from IndexedDB:', err);
        this.templatesLoadedSubject.next(true);
      })
      .finally(() => {
        this.dataRemoteService
          .getTemplates()
          .pipe(
            catchError((err) => {
              console.warn('[DataService] Template network refresh failed, keeping cached templates:', err);
              return of(null);
            }),
          )
          .subscribe((templates) => {
            if (templates) {
              this.allTemplates = templates;
              console.log(`[DataService] Refreshed ${templates.length} template(s) from server`);
            }
          });
      });
  }

  /**
   * Refresh template cache
   * Call this after creating, updating, or deleting templates
   */
  refreshTemplateCache(): void {
    this.loadTemplatesIntoCache();
  }

  private normalizeTagFromApi(rawTag: any): Tag {
    const rawColor = rawTag?.color ?? rawTag?.Color ?? rawTag?.tagColor ?? rawTag?.tag_color;
    const rawDescription = rawTag?.description ?? rawTag?.Description;
    return {
      _id: String(rawTag?._id ?? ''),
      _rev: rawTag?._rev ? String(rawTag._rev) : undefined,
      type: 'tag',
      name: String(rawTag?.name ?? ''),
      description: typeof rawDescription === 'string' ? rawDescription : undefined,
      tagType: String(rawTag?.tagType ?? rawTag?.tag_type ?? 'general'),
      slug: String(rawTag?.slug ?? ''),
      color: rawColor ? String(rawColor) : undefined,
      icon: rawTag?.icon ? String(rawTag.icon) : undefined,
      parentTagId: rawTag?.parentTagId ?? rawTag?.parent_tag_id ?? null,
      sortOrder: Number.isFinite(rawTag?.sortOrder) ? Number(rawTag.sortOrder) : Number(rawTag?.sort_order ?? 0),
      isActive: typeof rawTag?.isActive === 'boolean' ? rawTag.isActive : rawTag?.is_active !== false,
      // Kept exactly as the server sent it: only PRIVATE/SPACE are current, anything else (e.g. no
      // scope at all) stays undefined so filterCurrentTags() drops it as obsolete.
      scope: rawTag?.scope === TAG_SCOPE.SPACE || rawTag?.scope === TAG_SCOPE.PRIVATE ? rawTag.scope : undefined,
      spaceId: rawTag?.scope === TAG_SCOPE.SPACE && rawTag?.spaceId ? String(rawTag.spaceId) : null,
    };
  }

  /** Public for ScopedTagsService, which fetches Space tag lists outside this service. */
  normalizeTag(rawTag: any): Tag {
    return this.normalizeTagFromApi(rawTag);
  }

  /**
   * Offline-first fallback for getAllTags(): derives a unique, sorted tag list from
   * the Tags field of locally cached notes. Note.Tags entries may be a plain tag-id
   * string (no further detail available offline) or an embedded Tag object.
   */

  private serializeTagMutationPayload(payload: TagMutationPayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: payload.name,
      description: payload.description,
      tagType: payload.tagType,
      slug: payload.slug,
      color: payload.color,
      icon: payload.icon,
      isActive: payload.isActive,
    };

    if (payload.sortOrder !== undefined && payload.sortOrder !== null) {
      body['sort_order'] = payload.sortOrder;
    }

    if (payload.parentTagId !== undefined) {
      body['parent_tag_id'] = payload.parentTagId;
    }

    if (payload.spaceId) {
      body['spaceId'] = payload.spaceId;
    }

    return body;
  }

  private extractTagApiErrorMessage(err: any, fallback: string): string {
    const fieldErrors = err?.error?.errors?.json;
    if (fieldErrors && typeof fieldErrors === 'object') {
      const details = Object.entries(fieldErrors)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : String(messages)}`)
        .join(' | ');
      if (details) {
        return details;
      }
    }

    return err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? fallback;
  }

  private upsertTagCache(tag: Tag): void {
    const index = this.allTags.findIndex((existingTag) => existingTag._id === tag._id);
    if (index === -1) {
      this.allTags = [...this.allTags, tag].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      return;
    }

    const next = [...this.allTags];
    next[index] = tag;
    this.allTags = next.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }
}
